import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import type { Thing } from 'schema-dts';
import { PostCategory } from '@/entities';
import { ApiNewsRequest, getCommonPageProps, getNews, getPostCategories } from '@/shared/api';
import { getPageDetail } from '@/shared/api/page-detail';
import { getSiteSettings } from '@/shared/api/site-settings';
import JsonLd from '@/shared/components/shared/JsonLd';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateNewsJsonLd } from '@/shared/lib/utils/news-json-ld';
import NewsPageView, { NewsPageViewProps, newsPageLimits } from '@/views/NewsPageView';

const NewsCategoryPage = ({
    meta,
    h1,
    categorySlug,
    subscribeText,
    breadcrumbs,
    description,
    initialNewsResponse,
    categories,
    filter,
    mappedCategories,
    initialParams = {},
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    return (
        <>
            <NewsPageView
                key={categorySlug}
                meta={meta}
                h1={h1}
                categorySlug={categorySlug}
                subscribeText={subscribeText}
                description={description}
                initialNewsResponse={initialNewsResponse}
                initialParams={initialParams}
                categories={categories}
                filter={filter}
                breadcrumbs={breadcrumbs}
                mappedCategories={mappedCategories}
            />
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-news-category"
                data={generateNewsJsonLd({
                    h1,
                    pageDescription: description,
                    listingPath: `/news/${categorySlug}/`,
                    articles: initialNewsResponse.data?.items || [],
                    breadcrumbs,
                })}
            />
        </>
    );
};

export default NewsCategoryPage;

type NewsCategoryPageProps = NonUndefined<CommonPageProps & NewsPageViewProps>;

export const getServerSideProps: GetServerSideProps<NewsCategoryPageProps> = async ({ query }) => {
    const categorySlug = query.categorySlug as string;

    const postCategoriesResponse = await getPostCategories();
    const categories = postCategoriesResponse.data || [];

    if (!categories.find((category) => category.slug === categorySlug)) {
        return { notFound: true };
    }

    const categoryId = categories.find((category) => category.slug === categorySlug)?.id || -1;

    const initialParams: ApiNewsRequest = {
        limit: newsPageLimits[0],
    };

    if (typeof query.limit === 'string') {
        initialParams.limit = parseInt(query.limit);

        if (!newsPageLimits.includes(initialParams.limit)) {
            initialParams.limit = newsPageLimits[0];
        }
    }

    if (typeof query.page === 'string') {
        initialParams.page = parseInt(query.page);
    }

    if (typeof query.q === 'string') {
        initialParams.q = query.q;
    }

    const [commonPageProps, pageResponse, newsResponse, siteSettings] = await Promise.all([
        getCommonPageProps(),
        getPageDetail({ params: { slug: 'posts' } }),
        getNews({ params: { ...initialParams, categories: [categoryId] }, postCategories: categories }),
        getSiteSettings({
            params: {
                keys: ['post_first_screen_title', 'post_first_screen_desription', 'post_first_screen_button_text'],
            },
        }),
    ]);

    const addedAllCategory =
        categories.length > 1 ? [{ name: 'Все', slug: '', id: '' } satisfies PostCategory, ...categories] : [];
    const mappedCategories = addedAllCategory.map((item) => ({ name: item.name }));

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'news-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Новости', href: '/news/' },
                {
                    text: categories.find((category) => category.slug === categorySlug)?.name || '',
                    href: `/news/${categorySlug}/`,
                },
            ],
            categorySlug,
            categories,
            mappedCategories,
            filter: addedAllCategory,
            initialNewsResponse: newsResponse,
            initialParams,
            h1: siteSettings.data?.post_first_screen_title || 'Новости форума и партнеров',
            subscribeText: siteSettings.data?.post_first_screen_button_text || 'Подписаться',
            description:
                siteSettings.data?.post_first_screen_desription ||
                'Подпишитесь на рассылку, чтобы быть в курсе актуальных новостей',
        },
    };
};
